import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles, X, Loader2, Mail } from "lucide-react";
import { api, onPaywall } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function Paywall() {
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);
    const [resent, setResent] = useState(false);
    const nav = useNavigate();
    const { user } = useAuth();

    useEffect(() => onPaywall((d) => { setDetail(d); setOpen(true); setResent(false); }), []);

    if (!open || !detail) return null;

    const isVerify = detail.code === "email_verification_required";

    const close = () => setOpen(false);
    const subscribe = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
            window.location.href = data.url;
        } catch (e) {
            alert("Could not start checkout: " + (e.response?.data?.detail || e.message));
            setBusy(false);
        }
    };
    const resendVerify = async () => {
        if (!user?.email) return;
        setBusy(true);
        try {
            await api.post("/auth/resend-verification", { email: user.email });
            setResent(true);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/45 grid place-items-center p-4" data-testid="paywall-modal"
             onClick={close}>
            <div onClick={(e) => e.stopPropagation()}
                 className="card-warm w-full max-w-md p-6 relative border-l-4"
                 style={{ borderLeftColor: isVerify ? "var(--brand-orange)" : "var(--brand-magenta)" }}>
                <button onClick={close} aria-label="Close"
                        className="absolute top-3 right-3 text-[var(--text-ink)]/55 hover:text-[var(--brand-magenta)]"
                        data-testid="paywall-close">
                    <X size={18} />
                </button>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full sparkle-badge text-xs mb-3">
                    {isVerify ? <Mail size={12} /> : <Lock size={12} />}
                    {isVerify ? "Verify your email" : "Trial ended"}
                </div>
                <div className="font-serif text-2xl">
                    {isVerify ? "Confirm your email first" : "Unlock your AI superpowers"}
                </div>
                <div className="text-sm text-[var(--text-ink)]/70 mt-2">
                    {detail.message ||
                        (isVerify
                            ? "Please verify your email address to unlock AI features."
                            : "Your free trial has ended. Subscribe to keep using AI features.")}
                </div>
                {!isVerify && (
                    <ul className="text-sm mt-4 space-y-1.5">
                        {[
                            "AI Meeting Notes, Action Items & Follow-up Emails",
                            "Client Memory Ask-AI grounded in your data",
                            "Study Hub: Whisper transcripts + Smart Notes",
                            "Missed Lecture Mode with AI Tutor",
                            "Assessment Tools (Proofread, Improve Flow, Reference Guide)",
                        ].map((f) => (
                            <li key={f} className="flex items-start gap-2">
                                <Sparkles size={12} className="text-[var(--brand-orange)] mt-1 shrink-0" /> {f}
                            </li>
                        ))}
                    </ul>
                )}
                {isVerify && (
                    <div className="text-sm mt-4 text-[var(--text-ink)]/75">
                        We sent a verification link to <strong>{user?.email}</strong>. Open it from your inbox, then come back here.
                        {resent && <div className="mt-2 text-[var(--status-green)]">A fresh link has just been sent.</div>}
                    </div>
                )}
                <div className="mt-5 flex gap-2 justify-end">
                    {isVerify ? (
                        <>
                            <button onClick={close} className="btn-ghost text-sm" data-testid="verify-modal-close">
                                Close
                            </button>
                            <button onClick={resendVerify} disabled={busy || resent}
                                    data-testid="verify-modal-resend"
                                    className="btn-primary text-sm inline-flex items-center gap-2">
                                {busy && <Loader2 size={14} className="animate-spin" />}
                                {resent ? "Sent" : busy ? "Sending…" : "Resend verification link"}
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => { close(); nav("/billing"); }}
                                    className="btn-ghost text-sm" data-testid="paywall-billing">
                                See plan
                            </button>
                            <button onClick={subscribe} disabled={busy} className="btn-primary text-sm inline-flex items-center gap-2"
                                    data-testid="paywall-subscribe">
                                {busy && <Loader2 size={14} className="animate-spin" />}
                                {busy ? "Redirecting…" : "Subscribe now"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
